import React, { useState, useEffect, useCallback } from 'react';
import { bookingAPI } from '../../services/crmAPI';
import CRMSubNav from './CRMSubNav';
import './CRM.css';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

interface AvailabilityRule {
  id?: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface MeetingType {
  id: string;
  name: string;
  slug: string;
  duration_minutes: number;
  description: string;
  is_active: boolean;
  color: string;
}

interface Blockout {
  id: string;
  date: string;
  start_time: string | null;
  end_time: string | null;
  reason: string;
  all_day: boolean;
}

interface Config {
  id: string;
  is_active: boolean;
  timezone: string;
  min_notice_hours: number;
  max_advance_days: number;
  buffer_minutes: number;
  default_video_link: string;
  video_provider: 'none' | 'teams' | 'custom';
  teams_tenant_id: string;
  teams_client_id: string;
  teams_client_secret_set: boolean;
  teams_user_id: string;
}

type Tab = 'availability' | 'meeting_types' | 'blockouts' | 'settings';

const BookingSettings: React.FC = () => {
  const [tab, setTab] = useState<Tab>('availability');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Availability state — initialize with 7 days so checkboxes are always interactive
  const [availability, setAvailability] = useState<AvailabilityRule[]>(() => {
    const activeDays = new Set([1, 3, 4]); // Tue, Thu, Fri
    return Array.from({ length: 7 }, (_, i) => ({
      day_of_week: i,
      start_time: '09:00',
      end_time: '17:00',
      is_active: activeDays.has(i),
    }));
  });

  // Meeting types state
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [newMT, setNewMT] = useState({ name: '', slug: '', duration_minutes: 30, description: '', color: '#8B0000' });
  const [showNewMT, setShowNewMT] = useState(false);

  // Blockouts state
  const [blockouts, setBlockouts] = useState<Blockout[]>([]);
  const [newBlockout, setNewBlockout] = useState({ date: '', reason: '', all_day: true, start_time: '', end_time: '' });

  // Config state
  const [config, setConfig] = useState<Config | null>(null);
  const [teamsSecret, setTeamsSecret] = useState('');
  const [teamsTestResult, setTeamsTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testingTeams, setTestingTeams] = useState(false);

  const showMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch config FIRST — this triggers auto-create if no config exists,
      // ensuring subsequent requests don't race to create the same config.
      const cfgRes = await bookingAPI.getConfig();
      setConfig({
        video_provider: 'custom',
        teams_tenant_id: '',
        teams_client_id: '',
        teams_client_secret_set: false,
        teams_user_id: '',
        ...cfgRes.data,
      });

      // Now fetch the rest in parallel — config guaranteed to exist
      const [availRes, mtRes, boRes] = await Promise.all([
        bookingAPI.getAvailability(),
        bookingAPI.getMeetingTypes(),
        bookingAPI.getBlockouts(),
      ]);

      if (Array.isArray(availRes.data) && availRes.data.length > 0) {
        const serverRules: AvailabilityRule[] = availRes.data;
        const byDay = new Map(serverRules.map((r: AvailabilityRule) => [r.day_of_week, r]));
        const full: AvailabilityRule[] = [];
        for (let i = 0; i < 7; i++) {
          full.push(byDay.get(i) || { day_of_week: i, start_time: '09:00', end_time: '17:00', is_active: false });
        }
        setAvailability(full);
      } else {
        initializeDefaults();
      }
      setMeetingTypes(mtRes.data || []);
      setBlockouts(boRes.data || []);
    } catch (err: any) {
      console.error('Failed to load booking settings:', err);
      initializeDefaults();
    } finally {
      setLoading(false);
    }
  }, []);

  const initializeDefaults = () => {
    // Default availability: Tue/Thu/Fri 9am-5pm Pacific
    const activeDays = new Set([1, 3, 4]); // Tue, Thu, Fri
    const defaults: AvailabilityRule[] = [];
    for (let i = 0; i < 7; i++) {
      defaults.push({
        day_of_week: i,
        start_time: '09:00',
        end_time: '17:00',
        is_active: activeDays.has(i),
      });
    }
    setAvailability(defaults);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ===== Availability =====
  const handleAvailChange = (dayIdx: number, field: string, value: any) => {
    console.log(`[BookingSettings] handleAvailChange day=${dayIdx} field=${field} value=${value}`);
    setAvailability(prev => {
      const exists = prev.some(r => r.day_of_week === dayIdx);
      if (exists) {
        return prev.map(r => r.day_of_week === dayIdx ? { ...r, [field]: value } : r);
      }
      return [...prev, { day_of_week: dayIdx, start_time: '09:00', end_time: '17:00', is_active: false, [field]: value }];
    });
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      const res = await bookingAPI.updateAvailability(availability);
      setAvailability(res.data);
      showMessage('Availability saved!');
    } catch (err) {
      showMessage('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  // ===== Meeting Types =====
  const handleCreateMT = async () => {
    if (!newMT.name || !newMT.slug) return;
    setSaving(true);
    try {
      const res = await bookingAPI.createMeetingType(newMT);
      setMeetingTypes(prev => [...prev, res.data]);
      setNewMT({ name: '', slug: '', duration_minutes: 30, description: '', color: '#8B0000' });
      setShowNewMT(false);
      showMessage('Meeting type created!');
    } catch (err: any) {
      showMessage(err.response?.data?.error || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMT = async (mt: MeetingType) => {
    try {
      await bookingAPI.updateMeetingType(mt.id, { is_active: !mt.is_active });
      setMeetingTypes(prev => prev.map(m => m.id === mt.id ? { ...m, is_active: !m.is_active } : m));
    } catch (err) {
      showMessage('Failed to update');
    }
  };

  const handleDeleteMT = async (mt: MeetingType) => {
    if (!window.confirm(`Delete "${mt.name}"? This cannot be undone.`)) return;
    try {
      await bookingAPI.deleteMeetingType(mt.id);
      setMeetingTypes(prev => prev.filter(m => m.id !== mt.id));
      showMessage('Deleted');
    } catch (err) {
      showMessage('Failed to delete');
    }
  };

  const copyBookingUrl = (slug: string) => {
    const url = `${window.location.origin}/book/${slug}`;
    navigator.clipboard.writeText(url).then(() => showMessage('Link copied!'));
  };

  // ===== Blockouts =====
  const handleCreateBlockout = async () => {
    if (!newBlockout.date) return;
    setSaving(true);
    try {
      const payload: Record<string, any> = {
        date: newBlockout.date,
        reason: newBlockout.reason,
      };
      if (!newBlockout.all_day) {
        payload.start_time = newBlockout.start_time;
        payload.end_time = newBlockout.end_time;
      }
      const res = await bookingAPI.createBlockout(payload);
      setBlockouts(prev => [...prev, res.data]);
      setNewBlockout({ date: '', reason: '', all_day: true, start_time: '', end_time: '' });
      showMessage('Blockout added!');
    } catch (err) {
      showMessage('Failed to add blockout');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlockout = async (id: string) => {
    try {
      await bookingAPI.deleteBlockout(id);
      setBlockouts(prev => prev.filter(b => b.id !== id));
      showMessage('Blockout removed');
    } catch (err) {
      showMessage('Failed to remove');
    }
  };

  // ===== Config =====
  const handleConfigChange = (field: string, value: any) => {
    if (!config) return;
    setConfig({ ...config, [field]: value });
  };

  const saveConfig = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const payload: Record<string, any> = { ...config };
      if (teamsSecret) {
        payload.teams_client_secret = teamsSecret;
      }
      const res = await bookingAPI.updateConfig(payload);
      setConfig(res.data);
      if (teamsSecret) setTeamsSecret('');
      showMessage('Settings saved!');
    } catch (err) {
      showMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestTeams = async () => {
    setTestingTeams(true);
    setTeamsTestResult(null);
    try {
      const res = await bookingAPI.testTeamsConnection();
      setTeamsTestResult(res.data);
    } catch (err) {
      setTeamsTestResult({ success: false, message: 'Connection test failed' });
    } finally {
      setTestingTeams(false);
    }
  };

  if (loading) {
    return (
      <div className="crm-page">
        <CRMSubNav />
        <h1>Booking Settings</h1>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="crm-page">
      <CRMSubNav />
      <div className="crm-header">
        <h1>Booking Settings</h1>
      </div>

      {message && (
        <div style={{
          padding: '0.75rem 1rem',
          background: message.includes('Failed') ? '#fff5f5' : '#f0f7ee',
          color: message.includes('Failed') ? '#8B0000' : '#2D5A27',
          borderRadius: '6px',
          marginBottom: '1rem',
          fontWeight: 500,
        }}>
          {message}
        </div>
      )}

      <div className="booking-settings-tabs">
        {(['availability', 'meeting_types', 'blockouts', 'settings'] as Tab[]).map(t => (
          <button
            key={t}
            className={`booking-settings-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'meeting_types' ? 'Meeting Types' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Availability Tab */}
      {tab === 'availability' && (
        <div className="crm-card" style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 1rem 0' }}>Weekly Availability</h3>
          <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 1rem 0' }}>
            Set your available hours for each day of the week.
          </p>

          {DAY_NAMES.map((day, idx) => {
            const rule = availability.find(r => r.day_of_week === idx) || {
              day_of_week: idx, start_time: '09:00', end_time: '17:00', is_active: false
            };
            return (
              <div key={idx} className="booking-avail-row">
                <div className="booking-avail-day">{day}</div>
                <div className="booking-avail-toggle">
                  <input
                    type="checkbox"
                    checked={rule.is_active}
                    onChange={e => handleAvailChange(idx, 'is_active', e.target.checked)}
                  />
                </div>
                {rule.is_active && (
                  <div className="booking-avail-times">
                    <input
                      type="time"
                      value={rule.start_time}
                      onChange={e => handleAvailChange(idx, 'start_time', e.target.value)}
                    />
                    <span>to</span>
                    <input
                      type="time"
                      value={rule.end_time}
                      onChange={e => handleAvailChange(idx, 'end_time', e.target.value)}
                    />
                  </div>
                )}
                {!rule.is_active && (
                  <span style={{ color: '#999', fontSize: '0.9rem' }}>Unavailable</span>
                )}
              </div>
            );
          })}

          <div style={{ marginTop: '1.25rem' }}>
            <button onClick={saveAvailability} className="crm-btn crm-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Availability'}
            </button>
          </div>
        </div>
      )}

      {/* Meeting Types Tab */}
      {tab === 'meeting_types' && (
        <div>
          {meetingTypes.map(mt => (
            <div key={mt.id} className="booking-mt-card">
              <div className="booking-mt-info">
                <div className="booking-mt-name">
                  <span className="booking-mt-color-dot" style={{ backgroundColor: mt.color }} />
                  {mt.name}
                  {!mt.is_active && <span style={{ color: '#999', fontSize: '0.8rem' }}>(inactive)</span>}
                </div>
                <div className="booking-mt-meta">
                  {mt.duration_minutes} min &middot; /book/{mt.slug}
                  {mt.description && <> &middot; {mt.description}</>}
                </div>
              </div>
              <div className="booking-mt-actions">
                <button onClick={() => copyBookingUrl(mt.slug)} className="crm-btn crm-btn-sm">
                  Copy Link
                </button>
                <button
                  onClick={() => handleToggleMT(mt)}
                  className={`crm-btn crm-btn-sm ${mt.is_active ? '' : 'crm-btn-primary'}`}
                >
                  {mt.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button onClick={() => handleDeleteMT(mt)} className="crm-btn crm-btn-sm crm-btn-danger">
                  Delete
                </button>
              </div>
            </div>
          ))}

          {showNewMT ? (
            <div className="crm-card" style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', marginTop: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 1rem 0' }}>New Meeting Type</h3>
              <div className="booking-settings-field">
                <label>Name</label>
                <input
                  type="text"
                  value={newMT.name}
                  onChange={e => {
                    const name = e.target.value;
                    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                    setNewMT(prev => ({ ...prev, name, slug }));
                  }}
                  placeholder="Portal Walkthrough"
                />
              </div>
              <div className="booking-settings-field">
                <label>URL Slug</label>
                <input
                  type="text"
                  value={newMT.slug}
                  onChange={e => setNewMT(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="portal-walkthrough"
                />
                <div className="help-text">Booking URL: /book/{newMT.slug || 'slug'}</div>
              </div>
              <div className="booking-settings-field">
                <label>Duration (minutes)</label>
                <input
                  type="number"
                  value={newMT.duration_minutes}
                  onChange={e => setNewMT(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 30 }))}
                  min={15}
                  max={180}
                  style={{ maxWidth: '120px' }}
                />
              </div>
              <div className="booking-settings-field">
                <label>Description</label>
                <input
                  type="text"
                  value={newMT.description}
                  onChange={e => setNewMT(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="A quick walkthrough of your customer portal"
                />
              </div>
              <div className="booking-settings-field">
                <label>Color</label>
                <input
                  type="color"
                  value={newMT.color}
                  onChange={e => setNewMT(prev => ({ ...prev, color: e.target.value }))}
                  style={{ width: '50px', height: '35px', padding: '2px' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleCreateMT} className="crm-btn crm-btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create'}
                </button>
                <button onClick={() => setShowNewMT(false)} className="crm-btn">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowNewMT(true)} className="crm-btn crm-btn-primary" style={{ marginTop: '1rem' }}>
              + New Meeting Type
            </button>
          )}
        </div>
      )}

      {/* Blockouts Tab */}
      {tab === 'blockouts' && (
        <div>
          <div className="crm-card" style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Add Blockout</h3>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="booking-settings-field" style={{ marginBottom: 0 }}>
                <label>Date</label>
                <input
                  type="date"
                  value={newBlockout.date}
                  onChange={e => setNewBlockout(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div className="booking-settings-field" style={{ marginBottom: 0 }}>
                <label>
                  <input
                    type="checkbox"
                    checked={newBlockout.all_day}
                    onChange={e => setNewBlockout(prev => ({ ...prev, all_day: e.target.checked }))}
                  /> All day
                </label>
              </div>
              {!newBlockout.all_day && (
                <>
                  <div className="booking-settings-field" style={{ marginBottom: 0 }}>
                    <label>Start</label>
                    <input
                      type="time"
                      value={newBlockout.start_time}
                      onChange={e => setNewBlockout(prev => ({ ...prev, start_time: e.target.value }))}
                    />
                  </div>
                  <div className="booking-settings-field" style={{ marginBottom: 0 }}>
                    <label>End</label>
                    <input
                      type="time"
                      value={newBlockout.end_time}
                      onChange={e => setNewBlockout(prev => ({ ...prev, end_time: e.target.value }))}
                    />
                  </div>
                </>
              )}
              <div className="booking-settings-field" style={{ marginBottom: 0 }}>
                <label>Reason</label>
                <input
                  type="text"
                  value={newBlockout.reason}
                  onChange={e => setNewBlockout(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Holiday, vacation, etc."
                />
              </div>
              <button onClick={handleCreateBlockout} className="crm-btn crm-btn-primary" disabled={saving || !newBlockout.date}>
                Add
              </button>
            </div>
          </div>

          <div className="booking-blockout-list">
            {blockouts.length === 0 && (
              <p style={{ color: '#999' }}>No upcoming blockouts</p>
            )}
            {blockouts.map(b => (
              <div key={b.id} className="booking-blockout-item">
                <div>
                  <strong>
                    {new Date(b.date + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
                    })}
                  </strong>
                  {b.all_day ? (
                    <span style={{ color: '#999', marginLeft: '0.5rem' }}>All day</span>
                  ) : (
                    <span style={{ color: '#666', marginLeft: '0.5rem' }}>
                      {b.start_time} - {b.end_time}
                    </span>
                  )}
                  {b.reason && <span style={{ color: '#666', marginLeft: '0.5rem' }}>&mdash; {b.reason}</span>}
                </div>
                <button onClick={() => handleDeleteBlockout(b.id)} className="crm-btn crm-btn-sm crm-btn-danger">
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {tab === 'settings' && config && (
        <div className="crm-card" style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 1rem 0' }}>Calendar Settings</h3>

          <div className="booking-settings-field">
            <label>Calendar Active</label>
            <label style={{ fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={config.is_active}
                onChange={e => handleConfigChange('is_active', e.target.checked)}
              /> Booking calendar is accepting new appointments
            </label>
          </div>

          <div className="booking-settings-field">
            <label>Timezone</label>
            <select
              value={config.timezone}
              onChange={e => handleConfigChange('timezone', e.target.value)}
            >
              <option value="America/Los_Angeles">Pacific Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/New_York">Eastern Time</option>
            </select>
          </div>

          <div className="booking-settings-field">
            <label>Minimum Notice (hours)</label>
            <input
              type="number"
              value={config.min_notice_hours}
              onChange={e => handleConfigChange('min_notice_hours', parseInt(e.target.value) || 24)}
              min={1}
              max={168}
              style={{ maxWidth: '120px' }}
            />
            <div className="help-text">How many hours in advance customers must book</div>
          </div>

          <div className="booking-settings-field">
            <label>Maximum Advance Booking (days)</label>
            <input
              type="number"
              value={config.max_advance_days}
              onChange={e => handleConfigChange('max_advance_days', parseInt(e.target.value) || 30)}
              min={1}
              max={365}
              style={{ maxWidth: '120px' }}
            />
            <div className="help-text">How far ahead customers can book</div>
          </div>

          <div className="booking-settings-field">
            <label>Buffer Between Meetings (minutes)</label>
            <input
              type="number"
              value={config.buffer_minutes}
              onChange={e => handleConfigChange('buffer_minutes', parseInt(e.target.value) || 15)}
              min={0}
              max={60}
              style={{ maxWidth: '120px' }}
            />
            <div className="help-text">Padding between consecutive meetings</div>
          </div>

          <div className="booking-settings-field">
            <label>Video Provider</label>
            <select
              value={config.video_provider}
              onChange={e => handleConfigChange('video_provider', e.target.value)}
            >
              <option value="none">No video link</option>
              <option value="teams">Microsoft Teams (auto-create)</option>
              <option value="custom">Custom link (static)</option>
            </select>
          </div>

          {config.video_provider === 'custom' && (
            <div className="booking-settings-field">
              <label>Default Video Link</label>
              <input
                type="url"
                value={config.default_video_link}
                onChange={e => handleConfigChange('default_video_link', e.target.value)}
                placeholder="https://meet.google.com/..."
                style={{ maxWidth: '400px' }}
              />
              <div className="help-text">Auto-added to all new bookings</div>
            </div>
          )}

          {config.video_provider === 'teams' && (
            <div style={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              background: '#fafafa',
            }}>
              <h4 style={{ margin: '0 0 0.75rem 0', color: '#5059C9' }}>
                Microsoft Teams Integration
              </h4>
              <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
                Requires an Azure AD app registration with OnlineMeetings.ReadWrite.All permission.
              </p>

              <div className="booking-settings-field">
                <label>Tenant ID</label>
                <input
                  type="text"
                  value={config.teams_tenant_id}
                  onChange={e => handleConfigChange('teams_tenant_id', e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  style={{ maxWidth: '400px', fontFamily: 'monospace' }}
                />
              </div>

              <div className="booking-settings-field">
                <label>Client ID (Application ID)</label>
                <input
                  type="text"
                  value={config.teams_client_id}
                  onChange={e => handleConfigChange('teams_client_id', e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  style={{ maxWidth: '400px', fontFamily: 'monospace' }}
                />
              </div>

              <div className="booking-settings-field">
                <label>Client Secret</label>
                <input
                  type="password"
                  value={teamsSecret}
                  onChange={e => setTeamsSecret(e.target.value)}
                  placeholder={config.teams_client_secret_set ? '(saved - enter new to change)' : 'Enter client secret'}
                  style={{ maxWidth: '400px', fontFamily: 'monospace' }}
                />
                {config.teams_client_secret_set && !teamsSecret && (
                  <div className="help-text" style={{ color: '#2D5A27' }}>Secret is configured</div>
                )}
              </div>

              <div className="booking-settings-field">
                <label>Organizer User ID (UPN or Object ID)</label>
                <input
                  type="text"
                  value={config.teams_user_id}
                  onChange={e => handleConfigChange('teams_user_id', e.target.value)}
                  placeholder="brad@theredbridgefarm.com or user object ID"
                  style={{ maxWidth: '400px', fontFamily: 'monospace' }}
                />
                <div className="help-text">The Azure AD user who will be the meeting organizer</div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                <button
                  onClick={handleTestTeams}
                  className="crm-btn"
                  disabled={testingTeams || !config.teams_tenant_id || !config.teams_client_id}
                >
                  {testingTeams ? 'Testing...' : 'Test Connection'}
                </button>
                {teamsTestResult && (
                  <span style={{
                    fontSize: '0.85rem',
                    color: teamsTestResult.success ? '#2D5A27' : '#8B0000',
                    fontWeight: 500,
                  }}>
                    {teamsTestResult.message}
                  </span>
                )}
              </div>
            </div>
          )}

          <button onClick={saveConfig} className="crm-btn crm-btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}
    </div>
  );
};

export default BookingSettings;
